// Guarded localStorage access.
//
// localStorage can throw at every call site (Safari private mode, disabled
// cookies, sandboxed iframes), so the storage calls themselves are wrapped —
// not just JSON.parse/stringify.

const removeQuietly = (key) => {
  try {
    localStorage.removeItem(key);
  } catch {
    // storage unavailable — nothing to remove
  }
};

/**
 * Read and validate a JSON value from localStorage.
 *
 * Returns `fallback` when the key is absent, storage is unavailable, the
 * value fails to parse, or `validate(parsed)` is falsy. Parse and validation
 * failures also remove the offending key, so the guard does not silently
 * re-fire on every mount forever. Never throws on storage or data failures.
 *
 * @param {string} key
 * @param {(parsed: unknown) => boolean} validate required — no default
 * @param {*} fallback
 */
export const readGuarded = (key, validate, fallback) => {
  if (typeof validate !== 'function') {
    throw new TypeError('readGuarded: a validate function is required');
  }
  let raw;
  try {
    raw = localStorage.getItem(key);
  } catch {
    return fallback;
  }
  if (raw === null) {
    return fallback;
  }
  let parsed;
  try {
    parsed = JSON.parse(raw);
  } catch {
    removeQuietly(key);
    return fallback;
  }
  let valid;
  try {
    valid = Boolean(validate(parsed));
  } catch {
    valid = false;
  }
  if (!valid) {
    removeQuietly(key);
    return fallback;
  }
  return parsed;
};

/**
 * JSON-serialize and store a value. Returns true on success, false when
 * serialization or storage fails (circular value, quota, private mode).
 * Never throws.
 *
 * @param {string} key
 * @param {*} value
 * @returns {boolean}
 */
export const writeGuarded = (key, value) => {
  let serialized;
  try {
    serialized = JSON.stringify(value);
  } catch {
    return false;
  }
  try {
    localStorage.setItem(key, serialized);
  } catch {
    return false;
  }
  return true;
};
